/**
 * The sidebar's visibility rule.
 *
 * Lives outside Sidebar.tsx so it can be tested without mounting the sidebar
 * (which needs Keycloak, the router and the store). The test used to carry its
 * own copy of this logic, which meant it kept passing while the real sidebar
 * had two bugs: a section that hid every link beneath it, and sections that
 * showed for users who could not open anything inside them.
 */

export interface NavGate {
  /** Visible only if the user holds this permission. */
  requiredPermission?: string;
  /**
   * Visible if the user holds ANY of these. For sections hosting several
   * independently-gated pages, where demanding one specific permission would
   * hide the others.
   */
  requiredAnyPermission?: string[];
}

export interface NavLink extends NavGate {
  name: string;
  href?: string;
  submenu?: Array<NavGate & { name: string; href?: string }>;
}

export interface NavSection<TItem extends NavLink = NavLink> extends NavGate {
  id: string;
  href?: string;
  items: TItem[];
}

/** Whether a single entry passes its own permission gate. */
export function isVisible(entry: NavGate, has: (permission: string) => boolean) {
  if (entry.requiredAnyPermission?.length) {
    return entry.requiredAnyPermission.some(has);
  }
  return !entry.requiredPermission || has(entry.requiredPermission);
}

/**
 * Filters sections, then the links inside them.
 *
 * A section that declares links survives only if at least one of them did.
 * Sections that are a single destination (no links, e.g. Analytics) survive on
 * their href, and carry their own permission where one applies. Surviving
 * sections point at their first visible link so the header never navigates to
 * a page the user cannot open.
 */
export function filterNav<TSection extends NavSection>(
  sections: TSection[],
  has: (permission: string) => boolean,
): TSection[] {
  const visible = (entry: NavGate) => isVisible(entry, has);

  return sections
    .filter(visible)
    .map((section) => ({
      ...section,
      items: (section.items ?? []).filter(visible).map((item) => ({
        ...item,
        submenu: item.submenu?.filter(visible),
      })),
    }))
    .filter((section) => {
      const declaresLinks =
        (sections.find((s) => s.id === section.id)?.items ?? []).length > 0;
      return declaresLinks ? section.items.length > 0 : Boolean(section.href);
    })
    .map((section) => ({
      ...section,
      href: section.items[0]?.href ?? section.href,
    }));
}

/** Builds a permission predicate from a resolved permission list. */
export function permissionChecker(held: string[]) {
  return (permission: string) =>
    held.includes('*') || held.includes(permission);
}
